import { useEffect, useState } from "react";
import { PiggyBank, Plus, Trash2, ArrowDown, ArrowUp } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function KoloPage() {
  const { user } = useAuth();
  const cur = user?.preferred_currency || "GBP";
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [actOpen, setActOpen] = useState(null); // {goal, kind}
  const [form, setForm] = useState({ name: "", target_amount: 100, target_date: "", note: "" });
  const [amount, setAmount] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kolo/goals");
      setGoals(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/kolo/goals", {
        name: form.name, target_amount: Number(form.target_amount),
        target_date: form.target_date || null, note: form.note || null,
      });
      toast.success("Goal created");
      setNewOpen(false);
      setForm({ name: "", target_amount: 100, target_date: "", note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create goal");
    }
  };

  const act = async () => {
    if (!actOpen || !amount) return;
    try {
      await api.post(`/kolo/goals/${actOpen.goal.goal_id}/${actOpen.kind}`, { amount: Number(amount) });
      toast.success(actOpen.kind === "deposit" ? "Saved!" : "Withdrawn");
      setActOpen(null); setAmount("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/kolo/goals/${id}`);
    toast.success("Goal deleted");
    load();
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Kolo · Personal savings</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Your savings goals</h1>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="kolo-new-goal"><Plus className="mr-1 h-4 w-4" /> New goal</Button>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
         goals.length === 0 ? (
           <div className="col-span-2 rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
             <PiggyBank className="mx-auto h-8 w-8 opacity-40" />
             <p className="mt-3">No goals yet. Create your first one to start saving.</p>
           </div>
         ) : goals.map((g) => {
           const pct = Math.min(100, Math.round((g.saved_amount / g.target_amount) * 100));
           return (
             <div key={g.goal_id} className="rounded-2xl border border-border bg-card p-5" data-testid={`goal-${g.goal_id}`}>
               <div className="flex items-start justify-between gap-3">
                 <div>
                   <h3 className="font-display text-lg">{g.name}</h3>
                   <p className="text-xs text-muted-foreground">{g.target_date ? `Target: ${g.target_date}` : "No deadline"}</p>
                 </div>
                 <Button variant="ghost" size="icon" onClick={() => remove(g.goal_id)} data-testid={`goal-del-${g.goal_id}`}>
                   <Trash2 className="h-4 w-4" />
                 </Button>
               </div>
               <div className="mt-4 flex items-baseline gap-2">
                 <span className="font-display text-2xl text-accent">{formatMoney(g.saved_amount, cur)}</span>
                 <span className="text-sm text-muted-foreground">/ {formatMoney(g.target_amount, cur)}</span>
               </div>
               <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
                 <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
               </div>
               <div className="mt-1 text-xs text-muted-foreground">{pct}% complete</div>
               <div className="mt-4 flex gap-2">
                 <Button size="sm" onClick={() => setActOpen({ goal: g, kind: "deposit" })} data-testid={`goal-deposit-${g.goal_id}`}><ArrowDown className="mr-1 h-3.5 w-3.5" /> Add money</Button>
                 <Button size="sm" variant="outline" onClick={() => setActOpen({ goal: g, kind: "withdraw" })} data-testid={`goal-withdraw-${g.goal_id}`}><ArrowUp className="mr-1 h-3.5 w-3.5" /> Withdraw</Button>
               </div>
             </div>
           );
         })}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="goal-create-dialog">
          <DialogHeader>
            <DialogTitle>New savings goal</DialogTitle>
            <DialogDescription>Track a target amount and watch the progress grow.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11" data-testid="goal-name" /></div>
            <div className="space-y-1.5"><Label>Target amount ({cur})</Label><Input type="number" step="0.01" min="1" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} required className="h-11" data-testid="goal-target" /></div>
            <div className="space-y-1.5"><Label>Target date (optional)</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="h-11" /></div>
            <Button type="submit" className="h-11 w-full" data-testid="goal-create-submit">Create goal</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!actOpen} onOpenChange={(o) => !o && setActOpen(null)}>
        <DialogContent data-testid="goal-action-dialog">
          <DialogHeader>
            <DialogTitle>{actOpen?.kind === "deposit" ? "Add money" : "Withdraw"}</DialogTitle>
            <DialogDescription>{actOpen?.goal?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount in ${cur}`} className="h-11" data-testid="goal-action-amount" />
            <Button onClick={act} className="h-11 w-full" data-testid="goal-action-submit">{actOpen?.kind === "deposit" ? "Add" : "Withdraw"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
